import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';

interface ConsentDisclosureProps {
  envelopeTitle: string;
  ownerName: string;
  disclosureText: string;
  /**
   * Retrieve the document as a real PDF and open it. Resolves once the
   * server has issued the URL — which is the moment the retrieval is
   * recorded. See the docblock on the access gate below.
   */
  onOpenDocument: () => Promise<void>;
  /** True once this recipient has retrieved the PDF at least once. */
  accessDemonstrated: boolean;
  /**
   * Whether the access step is REQUIRED before consenting. Regulated tier
   * only — see the gate comment below. When false the step is still offered,
   * because being able to read the document before agreeing to it is useful
   * regardless of which tier it is; it simply does not block.
   */
  requiresAccessDemonstration: boolean;
  onAccept: () => void | Promise<void>;
  /** Refuse the AGREEMENT itself. */
  onDecline: () => void;
  /**
   * Refuse to sign ELECTRONICALLY and ask for a paper copy. A distinct act
   * from `onDecline` — see the component docblock.
   */
  onDeclineElectronic: () => void;
  /**
   * Whether to SURFACE the paper-copy election as a control. Regulated tier
   * only: § 7001(c)(1)(B)(i) engages where a consumer must receive something
   * in writing, which the Standard tier does not reach. The option is not
   * hidden on Standard — the disclosure text still states it — it simply is
   * not promoted to a third button on the most legally loaded screen here.
   */
  offerPaperCopy: boolean;
  pending?: boolean;
}

/**
 * Pre-signing consent step. The text is the legally relevant disclosure for
 * US ESIGN/UETA compliance — the recipient must see it AND click an
 * affirmative checkbox before any field becomes interactive. The accept
 * action also captures the recipient's IP + UA on the server.
 *
 * On the REGULATED tier there are three outcomes, not two. Declining to use
 * electronic records is not the same act as declining the agreement, so a
 * single "Decline" routing a paper request into a refusal-to-sign records
 * something the signer did not do and tells the sender their counterparty
 * walked away. The two are surfaced separately and recorded separately.
 *
 * On STANDARD there are two. § 7001(c)(1)(B)(i) — the disclosed right to
 * withdraw consent and obtain paper — engages only where a law requires a
 * consumer to receive something in writing, which business agreements do not
 * reach. The option is still stated in the disclosure text; it is simply not
 * promoted to a control for an entitlement that does not apply.
 */
export function ConsentDisclosure({
  envelopeTitle,
  ownerName,
  disclosureText,
  onOpenDocument,
  accessDemonstrated,
  requiresAccessDemonstration,
  onAccept,
  onDecline,
  onDeclineElectronic,
  offerPaperCopy,
  pending,
}: ConsentDisclosureProps) {
  const [checked, setChecked] = useState(false);
  const gateHintId = useId();
  const gateClosed = requiresAccessDemonstration && !accessDemonstrated;
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const handleOpen = async () => {
    setOpenError(null);
    setOpening(true);
    try {
      await onOpenDocument();
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : 'Could not open the document');
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Review and sign "{envelopeTitle}"</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {ownerName} has requested your signature. Before continuing, please review the disclosure
          below and confirm your consent to sign electronically.
        </p>
      </header>
      <section className="border rounded-md p-4 bg-muted/40">
        <h2 className="text-sm font-semibold mb-2">Electronic signature disclosure</h2>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{disclosureText}</p>
      </section>
      {/*
        The § 7001(c)(1)(C)(ii) access gate.

        Consent must be given in a way that reasonably demonstrates the signer
        can access the records in the form they will be delivered in. The
        checkbox below is the signer's own assertion, which demonstrates
        nothing. Opening the document does — and it must be the real PDF,
        because the signing view further on paints pages to a canvas with
        pdf.js, so a signer whose device cannot open a PDF would sail through
        that and then be unable to read the executed copy they are emailed.

        The retrieval is recorded server-side when the URL is issued; this
        gate is the prompt for it, not the evidence.
      */}
      <div className="border rounded-md p-4 space-y-2">
        <h2 className="text-sm font-semibold">Check you can open the document</h2>
        <p className="text-sm text-muted-foreground">
          Your signed copy will be delivered to you as a PDF.{' '}
          {requiresAccessDemonstration
            ? 'Open the document now to confirm you can read that format on this device.'
            : 'You can open it now to check you can read that format on this device.'}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={handleOpen} disabled={opening || pending}>
            {opening
              ? 'Opening…'
              : accessDemonstrated
                ? 'Open the document again'
                : 'Open the document (PDF)'}
          </Button>
          {accessDemonstrated && (
            <span className="text-sm text-muted-foreground" role="status">
              Opened — you can continue below.
            </span>
          )}
        </div>
        {openError && (
          <p className="text-sm text-destructive" role="alert">
            {openError}
          </p>
        )}
      </div>

      {requiresAccessDemonstration && !accessDemonstrated && (
        // A disabled checkbox with no stated reason is a dead end: the signer
        // clicks it, nothing happens, and nothing tells them what to do.
        // `role="status"` announces it when the gate closes; the id ties it to
        // the checkbox so a screen-reader user reaching the disabled control
        // hears the reason rather than finding it by chance.
        <p id={gateHintId} className="text-sm text-muted-foreground" role="status">
          Open the document above to continue.
        </p>
      )}
      <label
        className={`flex items-start gap-2 text-sm select-none ${
          gateClosed ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          disabled={gateClosed}
          aria-describedby={gateClosed ? gateHintId : undefined}
          className="mt-1"
        />
        <span>
          I have read the disclosure above and agree to use electronic records and signatures.
        </span>
      </label>
      {/* Explanation sits ABOVE the controls. People decide at the buttons, so
          copy distinguishing two similar-sounding refusals is no use
          underneath them. With one refusal on offer there is nothing to
          distinguish, so it collapses rather than stating the obvious. */}
      {offerPaperCopy && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground space-y-1">
          <p>
            <strong className="text-foreground">Request a paper copy</strong> — you would rather not
            sign electronically. This is not a refusal of the agreement.
          </p>
          <p>
            <strong className="text-foreground">Decline to sign</strong> — you do not agree to the
            document itself.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button onClick={onAccept} disabled={!checked || gateClosed || pending}>
          {pending ? 'Continuing…' : 'I agree, continue to sign'}
        </Button>
        {offerPaperCopy && (
          <Button variant="outline" onClick={onDeclineElectronic} disabled={pending}>
            Request a paper copy
          </Button>
        )}
        {/* The one terminal action a signer cannot undo. When it sits beside
            "Request a paper copy" — two similar-sounding refusals, one of
            which ends the agreement — it is de-emphasised and separated. With
            no lookalike beside it, it is an ordinary secondary action. */}
        <Button
          variant={offerPaperCopy ? 'ghost' : 'outline'}
          size={offerPaperCopy ? 'sm' : 'default'}
          className={offerPaperCopy ? 'text-muted-foreground ml-auto' : undefined}
          onClick={onDecline}
          disabled={pending}
        >
          Decline to sign
        </Button>
      </div>
    </div>
  );
}
